import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'

/**
 * Stable id on the lifecycle banner the app shell renders above every page.
 *
 * <p>It exists so a control the lock has disabled that has nowhere of its own to
 * put a sentence — the icon-only global "New Task" quick-create, in the shell's
 * own chrome — can point `aria-describedby` at the explanation already on screen
 * directly above it (IN-107). Anything with room beside it uses
 * `LifecycleLockNote`; this is the fallback, not the pattern.
 *
 * <p>It lives HERE and not in `LifecycleBanner.tsx` because the shell loads that
 * component lazily; importing the constant from there would drag the chunk back
 * into the main bundle for the sake of one string.
 */
export const LIFECYCLE_BANNER_ID = 'lifecycle-banner'

export type LifecycleState =
  | 'ACTIVE'
  | 'TRIAL_GRACE'
  | 'CANCELLED_GRACE'
  | 'READ_ONLY'
  | 'PENDING_DELETION'

export interface LifecycleResponse {
  state: LifecycleState
  graceEndsAt: string | null
  daysUntilGraceEnd: number | null
  /** Set only once the studio is closed and queued — the day the data actually goes. */
  deletionScheduledFor: string | null
  daysUntilDeletion: number | null
  /** app.inactive-org.pending-deletion-days — how long a closed studio gets. */
  deletionGraceDays: number
}

/**
 * The three states in which the backend refuses every write.
 *
 * <p>Kept in step with {@code WriteRateLimitInterceptor.isLocked} by hand — the
 * server is the enforcement and this is only the affordance. If the two ever
 * disagree the failure mode is a button that lies in one direction or the
 * other, never a write that should have been refused and wasn't.
 *
 * <p>TRIAL_GRACE is deliberately NOT here: a trial is writable to its last day
 * (Andras, 2026-09-07), which is exactly why the lock cannot simply be
 * "anything that isn't ACTIVE".
 */
const LOCKED_STATES: readonly LifecycleState[] = [
  'CANCELLED_GRACE',
  'READ_ONLY',
  'PENDING_DELETION',
]

/**
 * The org's lifecycle row. Shared by `LifecycleBanner` and by every page that
 * needs to know whether writes are refused, so they all read ONE cache entry —
 * the banner is rendered by the app shell on every authenticated page, so a
 * caller here is normally reading state that is already warm rather than
 * issuing a request of its own.
 */
export function useLifecycle() {
  return useQuery<LifecycleResponse>({
    queryKey: queryKeys.billing.lifecycle,
    queryFn: async () => {
      const { data } = await apiClient.get('/organization/lifecycle')
      return data
    },
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
  })
}

/**
 * True when this studio has no paid subscription standing behind it any more — it
 * cancelled, or an earlier sweep locked or closed it.
 *
 * <p>**Why this reads the same three states as the write gate, and must.** A
 * cancelled studio is reset to `plan = "TRIAL"` by
 * `StripeWebhookHandler.applySubscriptionDeleted`, so the plan field says exactly
 * what a brand-new studio's says. The lifecycle row is the only thing that tells
 * "hasn't paid yet" from "stopped paying", and the states in which it says
 * "stopped paying" are precisely the states in which writes are refused — that is
 * not a coincidence to be un-duplicated later, it is one fact with two
 * consequences. `TRIAL_GRACE` is excluded from both for the same single reason:
 * a trial in its grace window IS a trial, and is writable to its last day.
 *
 * <p>Derived from `LOCKED_STATES` rather than restating it, so the two readings
 * cannot drift apart.
 *
 * <p>IN-10: Settings → Billing greeted a cancelled studio with "TRIAL · 29d left ·
 * Full access until trial ends", one line under the shell's own "your studio is
 * read-only until Oct 8" bar — on the very page the banner's Reactivate button
 * links to.
 */
export function isSubscriptionEnded(state: LifecycleState | undefined | null): boolean {
  return !!state && LOCKED_STATES.includes(state)
}

/**
 * True when this studio's writes are refused (423) by the lifecycle gate.
 *
 * <p>Use it to render a create/edit control as unavailable INSTEAD of letting
 * the user fill a form and fail (p2-subscription F7). Always pair it with a
 * visible sentence saying why — see `LifecycleLockNote`. A disabled control
 * with no explanation is worse than an enabled one that fails, because there
 * is nothing left to act on.
 *
 * <p>Returns false while the query is still loading, on purpose: an
 * optimistically-enabled button that then fails with a clear toast beats a
 * button that is dead for a second on every page load of a healthy studio.
 */
export function useWritesLocked(): boolean {
  const { data } = useLifecycle()
  return !!data && LOCKED_STATES.includes(data.state)
}
